import { config } from '@iheartjane/config';
import {
  useAeropayBankConnection,
  useAeropayUser,
  useLinkAeropayBankAccount,
} from '@iheartjane/data-access/react-query';
import { trackError } from '@iheartjane/tracking';
import type { AerosyncWidget, WidgetEventSuccessType } from 'aerosync-web-sdk';
import { AerosyncEnvironment, initAeroSyncWidget } from 'aerosync-web-sdk';
import { useEffect, useRef, useState } from 'react';

import { getJwt } from '@jane/shared/auth';
import { useJaneUserId } from '@jane/shared/data-access';

interface UseAerosyncProps {
  /** Function called when the user exits the widget */
  onClose?(): void;

  /** Function called when bank account fails to link */
  onError(err: unknown): void;

  /** Function called when the widget finishes loading */
  onLoad?(): void;

  /** Function called when bank account is successfully linked */
  onSuccess?(): void;

  widgetContainerId: string;
}

interface SuccessPayload {
  ClientName: string;
  FILoginAcctId: string;
  user_id: string;
  user_password: string;
}

export const useAerosync = ({
  onClose,
  onError,
  onLoad,
  onSuccess,
  widgetContainerId,
}: UseAerosyncProps) => {
  const shouldClose = useRef(true);
  const [linkingBankAccount, setLinkingBankAccount] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const janeUserId = useJaneUserId();
  const jwt = getJwt();
  const { data: aeropayUserData } = useAeropayUser({
    janeUserId: janeUserId || null,
    jwt,
  });

  const { data: aeropayBankConnection } = useAeropayBankConnection(
    {
      aeropayUserId: aeropayUserData?.user_id,
      jwt,
    },
    { onError: (err) => onError(err) }
  );

  const { mutate: linkBankAccounts } = useLinkAeropayBankAccount({
    janeUserId: janeUserId as number,
    jwt,
  });
  const token = aeropayBankConnection?.token;

  const widgetRef = useRef<AerosyncWidget | null>(null);

  // The aerosync widget invokes its own onClose function when successfully linking a bank account.
  // We pass onClose the close modal function for when a user hits the X button.
  // However, we do not want to close the modal upon linking a bank account.
  const handleClose = () => {
    if (shouldClose.current) {
      onClose && onClose();
    }
  };

  const shadowRoot = document.querySelector('#shadow-host')?.shadowRoot;

  useEffect(() => {
    if (token) {
      const environment: AerosyncEnvironment =
        config.aeropayEnv === 'production'
          ? AerosyncEnvironment.Production
          : AerosyncEnvironment.Sandbox;

      const sharedProps = {
        token: token,
        elementId: widgetContainerId,
        iframeTitle: 'Connect',
        style: {
          width: '100%',
          height: '100%',
        },
        environment,
        consumerId: config.aeropayConsumerId,
        onEvent: function () {
          return null;
        },
        onLoad: function () {
          onLoad && onLoad();
        },
        onSuccess: async function (e: WidgetEventSuccessType) {
          shouldClose.current = false;
          const event = e as SuccessPayload;
          setLinkingBankAccount(true);
          try {
            linkBankAccounts(
              {
                aeropayUserId: event.user_id,
                aeropayUserPassword: event.user_password,
              },
              {
                onError: (err) => onError(err),
                onSuccess,
                onSettled: () => {
                  setLinkingBankAccount(false);
                },
              }
            );
          } catch (e) {
            setLinkingBankAccount(false);
            onError(e);
          }
        },
        onClose: handleClose,
        onError: (event: string) => {
          trackError(`Aerosync Error`, { error: event });
          onError && onError(event);
        },
      };

      const ref = initAeroSyncWidget({
        ...sharedProps,
        targetDocument: shadowRoot || undefined,
      });

      widgetRef.current = ref;
      setIsLoading(false);
    }
  }, [token, widgetContainerId]);

  return { isLoading, linkingBankAccount, widgetRef };
};
